import type { QueuedChatMessage } from './chat-pane.types'

export type FlushDecision =
  | { type: 'send-now'; item: QueuedChatMessage }
  | { type: 'user-stopped' }
  | { type: 'natural-flush'; item: QueuedChatMessage }
  | { type: 'no-flush' }

export interface FlushContext {
  sendNowPending: QueuedChatMessage | null
  userStopped: boolean
  queue: QueuedChatMessage[]
  isAgenticContinue: boolean
  streamCappedAt: string | null | undefined
  /** Row open in the composer. Holds natural flush only when it is the oldest row. */
  editingQueueId?: string | null
}

/**
 * Decide what to do with the queue after a stream ends.
 * Priority: Send now > User Stop > hold oldest while it is being edited >
 * natural flush (unless the turn ended on a Continue cap).
 *
 * Extracted for testing. Called from chat-pane.tsx handleSend finally block.
 */
export function decideQueueFlush(context: FlushContext): FlushDecision {
  // Priority 1: Send now takes precedence
  if (context.sendNowPending) {
    return { type: 'send-now', item: context.sendNowPending }
  }

  // Priority 2: User Stop means no flush
  if (context.userStopped) {
    return { type: 'user-stopped' }
  }

  // Priority 3: Natural end - flush oldest from queue (unless a Continue cap or Continue click).
  // Tool limit, round limit, and incomplete_write all show Continue. Flushing there
  // takes the row off the strip, then the Continue turn replaces the thread, so the
  // queued text never appears (incident 2026-09-24, dogfood 2026-10-25).
  // Also hold when the oldest row itself is open for edit.
  const skipQueueFlush =
    context.isAgenticContinue || Boolean(context.streamCappedAt)

  const oldest = context.queue[0]
  const editingOldest = Boolean(
    oldest?.id && context.editingQueueId && oldest.id === context.editingQueueId,
  )

  if (oldest && !skipQueueFlush && !editingOldest) {
    return { type: 'natural-flush', item: oldest }
  }

  return { type: 'no-flush' }
}
